import type { BasemapTileAddress } from "./draft";
import { BASEMAP_COARSEST_LEVEL, BASEMAP_TILE_PIXELS, type BasemapRasterRules } from "./manifest";

/** One tile of a raster, without the raster's name. */
export type TilePlace = Omit<BasemapTileAddress, "raster">;

export type RasterType = BasemapRasterRules["type"];

/** How many tiles cross a face at a level. */
export const tilesAcross = (level: number) => 2 ** (level - BASEMAP_COARSEST_LEVEL);

/** Every tile of every face at a level, faces first. */
export function placesAt(level: number, faces: number): TilePlace[] {
  const across = tilesAcross(level);
  const places: TilePlace[] = [];
  for (let face = 0; face < faces; face++) {
    for (let y = 0; y < across; y++) {
      for (let x = 0; x < across; x++) places.push({ face, level, x, y });
    }
  }
  return places;
}

/** The tile one level coarser that holds this one. */
export const parentOf = ({ face, level, x, y }: TilePlace): TilePlace => ({
  face,
  level: level - 1,
  x: x >> 1,
  y: y >> 1,
});

export const placeKey = ({ face, level, x, y }: TilePlace) => `${face}/${level}/${x}/${y}`;

/**
 * Where a tile sits in its PMTiles file. A sphere's faces take slots of the 4 × 4 grid at tile zoom 2,
 * face f at column f % 4 and row f ÷ 4; a plane's one face is the whole square.
 */
export function pmtilesAddress(
  { face, level, x, y }: TilePlace,
  geometry: "sphere" | "plane",
): { z: number; x: number; y: number } {
  if (geometry === "plane") return { z: level - BASEMAP_COARSEST_LEVEL, x, y };
  const across = tilesAcross(level);
  return { z: level - 7, x: (face % 4) * across + x, y: Math.floor(face / 4) * across + y };
}

/** Bytes each pixel takes in memory and in a draft blob. */
export const BYTES_PER_PIXEL: Record<RasterType, number> = {
  color: 4,
  category: 1,
  number: 4,
  elevation: 4,
};

/** A category pixel that holds no data. */
export const NO_CATEGORY = 255;
/** A number or elevation pixel that holds no data: the most negative count. */
export const NO_NUMBER = -(2 ** 31);

export const TILE_AREA = BASEMAP_TILE_PIXELS * BASEMAP_TILE_PIXELS;

/** A raster's pixels for one tile, as its type keeps them in memory. */
export type TilePixels = Uint8Array | Int32Array;

export function emptyTile(type: RasterType): TilePixels {
  if (type === "number" || type === "elevation") return new Int32Array(TILE_AREA).fill(NO_NUMBER);
  if (type === "category") return new Uint8Array(TILE_AREA).fill(NO_CATEGORY);
  return new Uint8Array(TILE_AREA * 4);
}

/**
 * A tile's pixels as the four channels of a 512-pixel image, which every published tile is. Color
 * is itself. A category id is repeated in red, green, and blue, with alpha full. A count's four bytes
 * go in red, green, blue, and alpha, most significant first.
 */
export function pixelsToImage(type: RasterType, pixels: TilePixels): Uint8ClampedArray {
  if (type === "color")
    return new Uint8ClampedArray(pixels.buffer, pixels.byteOffset, TILE_AREA * 4);
  const image = new Uint8ClampedArray(TILE_AREA * 4);
  if (type === "category") {
    for (let index = 0; index < TILE_AREA; index++) {
      const id = pixels[index] ?? NO_CATEGORY;
      image[index * 4] = id;
      image[index * 4 + 1] = id;
      image[index * 4 + 2] = id;
      image[index * 4 + 3] = 255;
    }
    return image;
  }
  const view = new DataView(image.buffer);
  for (let index = 0; index < TILE_AREA; index++)
    view.setInt32(index * 4, pixels[index] ?? NO_NUMBER);
  return image;
}

export function imageToPixels(type: RasterType, image: Uint8ClampedArray): TilePixels {
  if (type === "color") return new Uint8Array(image);
  if (type === "category") {
    const pixels = new Uint8Array(TILE_AREA);
    for (let index = 0; index < TILE_AREA; index++) pixels[index] = image[index * 4] ?? NO_CATEGORY;
    return pixels;
  }
  const pixels = new Int32Array(TILE_AREA);
  const view = new DataView(image.buffer, image.byteOffset, image.byteLength);
  for (let index = 0; index < TILE_AREA; index++) pixels[index] = view.getInt32(index * 4);
  return pixels;
}

/** A draft blob: a tile's pixels, little-endian, deflated. */
export async function tileToBlob(pixels: TilePixels): Promise<Uint8Array> {
  return transform(
    new Uint8Array(pixels.buffer, pixels.byteOffset, pixels.byteLength),
    new CompressionStream("deflate-raw"),
  );
}

export async function blobToTile(type: RasterType, blob: Uint8Array): Promise<TilePixels> {
  const bytes = await transform(blob, new DecompressionStream("deflate-raw"));
  return type === "number" || type === "elevation"
    ? new Int32Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 4)
    : bytes;
}

export async function transform(
  bytes: Uint8Array,
  stream: CompressionStream | DecompressionStream,
): Promise<Uint8Array> {
  const body = new Blob([bytes as Uint8Array<ArrayBuffer>]).stream().pipeThrough(stream);
  return new Uint8Array(await new Response(body).arrayBuffer());
}

/**
 * One tile one level coarser, from its four children in reading order, by the raster's downsampling
 * rule. A missing child holds no data.
 */
export function downsample(
  type: RasterType,
  children: readonly (TilePixels | null)[],
  weights: Readonly<Record<number, number>> = {},
): TilePixels {
  const parent = emptyTile(type);
  const half = BASEMAP_TILE_PIXELS / 2;
  for (let quadrant = 0; quadrant < 4; quadrant++) {
    const child = children[quadrant];
    if (!child) continue;
    const offsetX = (quadrant % 2) * half;
    const offsetY = Math.floor(quadrant / 2) * half;
    for (let y = 0; y < half; y++) {
      for (let x = 0; x < half; x++) {
        const target = (offsetY + y) * BASEMAP_TILE_PIXELS + offsetX + x;
        const source = [
          2 * y * BASEMAP_TILE_PIXELS + 2 * x,
          2 * y * BASEMAP_TILE_PIXELS + 2 * x + 1,
          (2 * y + 1) * BASEMAP_TILE_PIXELS + 2 * x,
          (2 * y + 1) * BASEMAP_TILE_PIXELS + 2 * x + 1,
        ];
        if (type === "color") meanColor(child, parent, source, target);
        else if (type === "category") commonestCategory(child, parent, source, target, weights);
        else meanCount(child as Int32Array, parent as Int32Array, source, target);
      }
    }
  }
  return parent;
}

const toLinear = new Float32Array(256).map((_, value) => {
  const c = value / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
});
const toSrgb = (linear: number) => {
  const c = linear <= 0.0031308 ? linear * 12.92 : 1.055 * linear ** (1 / 2.4) - 0.055;
  return Math.floor(Math.min(1, Math.max(0, c)) * 255 + 0.5);
};

/** The mean in linear light with premultiplied alpha. */
function meanColor(child: TilePixels, parent: TilePixels, source: number[], target: number) {
  let red = 0;
  let green = 0;
  let blue = 0;
  let alpha = 0;
  for (const index of source) {
    const a = (child[index * 4 + 3] ?? 0) / 255;
    red += (toLinear[child[index * 4] ?? 0] ?? 0) * a;
    green += (toLinear[child[index * 4 + 1] ?? 0] ?? 0) * a;
    blue += (toLinear[child[index * 4 + 2] ?? 0] ?? 0) * a;
    alpha += a;
  }
  if (alpha === 0) return;
  parent[target * 4] = toSrgb(red / alpha);
  parent[target * 4 + 1] = toSrgb(green / alpha);
  parent[target * 4 + 2] = toSrgb(blue / alpha);
  parent[target * 4 + 3] = Math.floor((alpha / 4) * 255 + 0.5);
}

/** The id with the largest count times weight, ties to the smaller id. */
function commonestCategory(
  child: TilePixels,
  parent: TilePixels,
  source: number[],
  target: number,
  weights: Readonly<Record<number, number>>,
) {
  let best = NO_CATEGORY;
  let bestScore = 0;
  for (const index of source) {
    const id = child[index] ?? NO_CATEGORY;
    if (id === NO_CATEGORY) continue;
    let count = 0;
    for (const other of source) if (child[other] === id) count++;
    const score = count * (weights[id] ?? 1);
    if (score > bestScore || (score === bestScore && id < best)) {
      best = id;
      bestScore = score;
    }
  }
  parent[target] = best;
}

/** The mean of the children that have data. */
function meanCount(child: Int32Array, parent: Int32Array, source: number[], target: number) {
  let sum = 0;
  let count = 0;
  for (const index of source) {
    const value = child[index] ?? NO_NUMBER;
    if (value === NO_NUMBER) continue;
    sum += value;
    count++;
  }
  if (count > 0) parent[target] = Math.round(sum / count);
}
